import * as RS from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from './cn'
import { useFieldControl } from './field-context'
import { controlBox } from './Input'
import { floatingPanel, menuItem } from './popover-styles'
import { usePrivateAttrs } from './privateArea'

export interface SelectOption {
  value: string
  label: ReactNode
  icon?: ReactNode
  description?: ReactNode
  disabled?: boolean
}

export interface SelectProps {
  options: SelectOption[]
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  name?: string
  id?: string
  disabled?: boolean
  required?: boolean
  invalid?: boolean
  size?: 'sm' | 'md'
  className?: string
  contentClassName?: string
  'aria-label'?: string
}

/** Single choice dropdown (Radix Select) styled like an input. Works inside <Field>. */
export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select',
  name,
  id,
  disabled,
  required,
  invalid,
  size = 'md',
  className,
  contentClassName,
  'aria-label': ariaLabel,
}: SelectProps) {
  const control = useFieldControl({ id, disabled, 'aria-invalid': invalid })
  const priv = usePrivateAttrs()
  return (
    <RS.Root value={value} defaultValue={defaultValue} onValueChange={onValueChange} name={name} disabled={control.disabled} required={required}>
      <RS.Trigger
        id={control.id}
        aria-label={ariaLabel}
        aria-invalid={control['aria-invalid']}
        aria-describedby={control['aria-describedby']}
        data-invalid={control.invalid || undefined}
        className={cn(
          controlBox,
          size === 'sm' ? 'h-8 max-sm:h-10 text-meta' : 'h-10',
          'cursor-pointer justify-between gap-2 px-3 text-left shadow-none outline-none',
          'focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus-halo data-[placeholder]:text-subtle',
          'disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:shrink-0',
          className,
        )}
      >
        {/* A block, not a flex box: text-overflow only reaches inline text, so a long translated value ("Hebdomadaire"
            in a 130 px trigger) ends with an ellipsis instead of being cut mid word (UXA-4). RS.Value takes no class. */}
        <span className="block min-w-0 truncate [&_svg]:size-4 [&_svg]:text-muted">
          <RS.Value placeholder={placeholder} />
        </span>
        <RS.Icon asChild>
          <ChevronDown className="size-4 text-muted" strokeWidth={1.75} />
        </RS.Icon>
      </RS.Trigger>
      <RS.Portal>
        <RS.Content
          {...priv}
          position="popper"
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            floatingPanel,
            'max-h-[min(360px,var(--radix-select-content-available-height))] min-w-(--radix-select-trigger-width) overflow-hidden',
            contentClassName,
          )}
        >
          <RS.Viewport className="p-1.5">
            {options.map((opt) => (
              <RS.Item key={opt.value} value={opt.value} disabled={opt.disabled} className={cn(menuItem, 'h-auto min-h-9 py-1.5 pr-8')}>
                {opt.icon}
                <span className="min-w-0 flex-1">
                  <RS.ItemText>{opt.label}</RS.ItemText>
                  {opt.description && <span className="block text-meta text-muted">{opt.description}</span>}
                </span>
                <RS.ItemIndicator className="absolute right-2.5 inline-flex">
                  <Check className="text-primary!" strokeWidth={2.25} />
                </RS.ItemIndicator>
              </RS.Item>
            ))}
          </RS.Viewport>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
  )
}
